import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { ChevronLeft, ChevronRight, ImageOff, Play, Volume2, VolumeX } from 'lucide-react';
import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { AVATAR_PALETTE } from '../../../../lib/avatar-color';
import {
  FACEBOOK_REEL_CAPTION_LIMIT,
  FACEBOOK_RENDITIONS,
  facebookCaptionSegments,
  facebookCaptionText,
  facebookFillsTallFrame,
  facebookGridRendition,
  facebookInitial,
  facebookLinkCardUrl,
  facebookLinkDomain,
  facebookLook,
  facebookMediaGrid,
  facebookMediaTotal,
  facebookPageName,
  facebookShowsNotShown,
  facebookTimingLine,
} from '../../../../lib/facebook-post-preview';
import { socialPostImageRendition, type SocialPostRendition } from '../../../../lib/social-post-preview-card';
import { Avatar, Badge, ScrollArea, TextButton } from '../../ui';
import styles from './FacebookPostPreview.module.css';

type MediaShape = { width: number; height: number };

export interface FacebookPostPreviewProps {
  card: SocialPostPreviewCardMetadata;
  onOpenMedia: (media: SocialPostPreviewMedia, index: number) => void;
  onToggleCaption: (expanded: boolean) => void;
  onOpenLink: () => void;
  onPortrait: (portrait: boolean) => void;
}

export function FacebookPostPreview(props: FacebookPostPreviewProps) {
  const look = facebookLook(props.card);
  if (look === 'reel') {
    return <FacebookReel {...props} />;
  }
  if (look === 'story') {
    return <FacebookStory key={props.card.media.map((item) => item.url).join(' ')} {...props} />;
  }
  return <FacebookFeedPost {...props} />;
}

function useTileLabel(card: SocialPostPreviewCardMetadata, count?: number) {
  const { t } = useTranslation();
  const total = count ?? facebookMediaTotal(card);
  return (media: SocialPostPreviewMedia, index: number, hidden = 0) => {
    const kind = media.kind === 'video' ? t('siteReview.element.video') : t('files.type.image');
    const place = total > 1 ? `${kind} ${t('wins.reel.count', { index: index + 1, total })}` : kind;
    return hidden > 0 ? `${place}, +${t('home.map.moreHidden', { count: hidden })}` : place;
  };
}

function PageAvatar({ card }: { card: SocialPostPreviewCardMetadata }) {
  const [failedUrl, setFailedUrl] = useState<string | undefined>(undefined);
  const name = facebookPageName(card);
  const pictureUrl = card.accountPictureUrl && card.accountPictureUrl !== failedUrl ? card.accountPictureUrl : null;
  return (
    <span className={styles.avatar} data-testid="facebook-avatar">
      <Avatar
        initials={facebookInitial(name)}
        photoUrl={pictureUrl}
        onPhotoError={() => setFailedUrl(card.accountPictureUrl)}
        size={24}
        color={AVATAR_PALETTE[0]}
      />
    </span>
  );
}

function FacebookFeedPost({ card, onOpenMedia, onToggleCaption, onOpenLink, onPortrait }: FacebookPostPreviewProps) {
  const { i18n } = useTranslation();
  const label = useTileLabel(card);
  const grid = facebookMediaGrid(card);
  const linkUrl = grid.kind === 'none' ? facebookLinkCardUrl(card.link) : undefined;
  return (
    <>
      <header className={styles.header}>
        <PageAvatar card={card} />
        <div className={styles.names}>
          <span className={styles.pageName} dir="auto" data-testid="facebook-page-name">
            {facebookPageName(card)}
          </span>
          <span className={styles.when} data-testid="facebook-when">
            {facebookTimingLine(card, i18n.language)}
          </span>
        </div>
        <Badge variant="waiting-on-a-reply" size="tiny" label={card.labels.badge} className={styles.pill} />
      </header>
      {card.caption && (
        <FacebookCaption
          caption={card.caption}
          seeMore={card.labels.seeMore}
          seeLess={card.labels.seeLess}
          className={styles.caption}
          onToggle={onToggleCaption}
        />
      )}
      {grid.kind === 'single' && grid.tile.kind === 'video' && (
        <FeedVideo media={grid.tile} label={label(grid.tile, 0)} onOpen={() => onOpenMedia(grid.tile, 0)} onPortrait={onPortrait} />
      )}
      {grid.kind === 'single' && grid.tile.kind === 'image' && (
        <FeedSingleImage key={grid.tile.url} media={grid.tile} label={label(grid.tile, 0)} onOpen={() => onOpenMedia(grid.tile, 0)} />
      )}
      {(grid.kind === 'two' || grid.kind === 'three' || grid.kind === 'four') && (
        <div className={`${styles.media} ${styles[grid.kind]}`} data-testid="facebook-media" data-layout={grid.kind}>
          {grid.tiles.map((media, index) => {
            const hidden = grid.kind === 'four' && index === grid.tiles.length - 1 ? grid.hidden : 0;
            return (
              <FacebookTile
                key={`${media.url}-${index}`}
                media={media}
                label={label(media, index, hidden)}
                rendition={facebookGridRendition(grid.kind, index)}
                hidden={hidden}
                onOpen={() => onOpenMedia(media, index)}
              />
            );
          })}
        </div>
      )}
      {facebookShowsNotShown(card) && (
        <p className={styles.notShown} data-testid="facebook-not-shown">
          {card.labels.mediaNotShown}
        </p>
      )}
      {linkUrl && <FacebookLinkCard link={linkUrl} onOpen={onOpenLink} />}
    </>
  );
}

function FacebookCaption({
  caption,
  seeMore,
  seeLess,
  className,
  limit,
  hashtags = true,
  scrollable = false,
  onToggle,
}: {
  caption: string;
  seeMore: string;
  seeLess: string;
  className: string;
  limit?: number;
  hashtags?: boolean;
  scrollable?: boolean;
  onToggle: (expanded: boolean) => void;
}) {
  const [expandedCaption, setExpandedCaption] = useState<string | null>(null);
  const expanded = expandedCaption === caption;
  const ref = useRef<HTMLElement>(null);
  const focusOnExpand = useRef(false);
  const { text, truncated } = facebookCaptionText(caption, expanded, limit);
  const expandable = expanded || truncated;
  const toggle = () => {
    focusOnExpand.current = !expanded;
    setExpandedCaption(expanded ? null : caption);
    onToggle(!expanded);
  };

  useEffect(() => {
    if (expanded && focusOnExpand.current) {
      focusOnExpand.current = false;
      ref.current?.focus();
    }
  }, [expanded]);

  const body = (
    <>
      {facebookCaptionSegments(text, { hashtags }).map((segment, index) =>
        segment.highlight ? (
          <span key={index} className={styles.highlight} dir={segment.text.startsWith('#') ? undefined : 'auto'}>
            {segment.text}
          </span>
        ) : (
          segment.text
        ),
      )}
      {expandable && (
        <>
          {' '}
          <TextButton size="small" className={styles.seeMore} aria-expanded={expanded} onClick={toggle}>
            {expanded ? seeLess : seeMore}
          </TextButton>
        </>
      )}
    </>
  );
  const shared = { className, dir: 'auto', tabIndex: -1, 'data-testid': 'facebook-caption', 'data-expanded': expanded };
  return scrollable ? (
    <ScrollArea ref={ref} component="p" {...shared}>
      {body}
    </ScrollArea>
  ) : (
    <p ref={ref as RefObject<HTMLParagraphElement>} {...shared}>
      {body}
    </p>
  );
}

function FeedSingleImage({ media, label, onOpen }: { media: SocialPostPreviewMedia; label: string; onOpen: () => void }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className={`${styles.media} ${styles.single}`}
      data-testid="facebook-media"
      data-layout="single"
      data-failed={failed}
    >
      <FacebookTile media={media} label={label} rendition={FACEBOOK_RENDITIONS.single} onFail={() => setFailed(true)} onOpen={onOpen} />
    </div>
  );
}

function FacebookTile({
  media,
  label,
  rendition,
  hidden = 0,
  onFail,
  onOpen,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition: SocialPostRendition;
  hidden?: number;
  onFail?: () => void;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const fail = () => {
    setFailed(true);
    onFail?.();
  };
  return (
    <button type="button" className={styles.tile} aria-label={label} data-kind={media.kind} onClick={onOpen}>
      {failed ? (
        <BrokenMedia covered={hidden > 0} />
      ) : media.kind === 'video' ? (
        <video className={styles.fill} src={media.url} preload="metadata" muted playsInline onError={fail} />
      ) : (
        <img className={styles.fill} src={socialPostImageRendition(media.url, rendition)} alt="" loading="lazy" onError={fail} />
      )}
      {media.kind === 'video' && !failed && hidden === 0 && <PlayGlyph />}
      {hidden > 0 && (
        <span className={styles.more} data-testid="facebook-more">
          +{hidden}
        </span>
      )}
    </button>
  );
}

function BrokenMedia({ covered = false }: { covered?: boolean }) {
  return (
    <span className={styles.placeholder} data-testid="facebook-media-placeholder">
      {!covered && <ImageOff size={20} aria-hidden="true" />}
    </span>
  );
}

function PlayGlyph() {
  return (
    <span className={styles.play} data-testid="facebook-play">
      <Play size={16} fill="currentColor" strokeWidth={0} aria-hidden="true" />
    </span>
  );
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function useInlineVideo(url: string) {
  const ref = useRef<HTMLVideoElement>(null);
  const [trackedUrl, setTrackedUrl] = useState(url);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [shape, setShape] = useState<MediaShape | null>(null);

  if (trackedUrl !== url) {
    setTrackedUrl(url);
    setFailed(false);
    setPlaying(false);
    setMuted(true);
    setShape(null);
  }

  useEffect(() => {
    if (ref.current) {
      ref.current.muted = muted;
    }
  }, [muted, url, failed]);

  useEffect(() => {
    const player = ref.current;
    if (!player || failed || prefersReducedMotion()) {
      return;
    }
    const play = () => {
      try {
        Promise.resolve(player.play()).catch(() => setPlaying(false));
      } catch {
        setPlaying(false);
      }
    };
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[entries.length - 1]?.isIntersecting) {
          play();
        } else {
          player.pause();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(player);
    return () => observer.disconnect();
  }, [url, failed]);

  const videoProps = {
    ref,
    src: url,
    loop: true,
    muted: true,
    playsInline: true,
    preload: 'metadata',
    onLoadedMetadata: (event: { currentTarget: HTMLVideoElement }) => {
      const { videoWidth, videoHeight } = event.currentTarget;
      setShape(videoWidth > 0 && videoHeight > 0 ? { width: videoWidth, height: videoHeight } : null);
    },
    onPlaying: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onError: () => setFailed(true),
  };
  return { failed, playing, muted, shape, toggleMute: () => setMuted((value) => !value), videoProps };
}

function SoundToggle({ muted, onToggle }: { muted: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={styles.sound}
      aria-label={muted ? t('voiceCall.unmute') : t('voiceCall.mute')}
      onClick={onToggle}
      data-testid="facebook-sound"
    >
      {muted ? <VolumeX size={14} aria-hidden="true" /> : <Volume2 size={14} aria-hidden="true" />}
    </button>
  );
}

function FeedVideo({
  media,
  label,
  onOpen,
  onPortrait,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  onOpen: () => void;
  onPortrait: (portrait: boolean) => void;
}) {
  const video = useInlineVideo(media.url);
  const portrait = !!video.shape && video.shape.height > video.shape.width;

  useEffect(() => {
    onPortrait(portrait);
    return () => onPortrait(false);
  }, [portrait, onPortrait]);

  const box: CSSProperties = { aspectRatio: video.shape ? `${video.shape.width} / ${video.shape.height}` : '16 / 9' };
  return (
    <div
      className={`${styles.media} ${styles.video}`}
      style={box}
      data-testid="facebook-media"
      data-layout="video"
      data-failed={video.failed}
    >
      <button type="button" className={styles.tile} aria-label={label} data-kind="video" onClick={onOpen}>
        {video.failed ? <BrokenMedia /> : <video className={styles.fill} {...video.videoProps} />}
        {!video.failed && !video.playing && <PlayGlyph />}
      </button>
      {!video.failed && video.playing && <SoundToggle muted={video.muted} onToggle={video.toggleMute} />}
    </div>
  );
}

function FacebookLinkCard({ link, onOpen }: { link: string; onOpen: () => void }) {
  return (
    <a className={styles.linkCard} href={link} target="_blank" rel="noopener noreferrer" dir="ltr" onClick={onOpen} data-testid="facebook-link-card">
      <span className={styles.domain} data-testid="facebook-link-domain">
        {facebookLinkDomain(link)}
      </span>
      <span className={styles.linkTitle}>{link}</span>
    </a>
  );
}

function TallFrame({ fits, children }: { fits: boolean; children: ReactNode }) {
  return (
    <div className={fits ? `${styles.fullMedia} ${styles.fitsFrame}` : styles.fullMedia} data-fits={fits}>
      {children}
    </div>
  );
}

function TallMedia({
  media,
  label,
  rendition,
  onOpen,
  onFit,
}: {
  media: SocialPostPreviewMedia;
  label: string;
  rendition: SocialPostRendition;
  onOpen: () => void;
  onFit: (fits: boolean) => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const video = useInlineVideo(media.url);
  const isVideo = media.kind === 'video';
  const failed = isVideo ? video.failed : imageFailed;

  useEffect(() => {
    if (isVideo) {
      onFit(!video.failed && !!video.shape && facebookFillsTallFrame(video.shape.width, video.shape.height));
    }
  }, [isVideo, video.failed, video.shape, onFit]);

  useEffect(() => () => onFit(false), [onFit]);

  return (
    <>
      <button type="button" className={styles.fullTile} aria-label={label} data-kind={media.kind} onClick={onOpen}>
        {failed ? (
          <BrokenMedia />
        ) : isVideo ? (
          <video className={styles.contain} {...video.videoProps} />
        ) : (
          <img
            className={styles.contain}
            src={socialPostImageRendition(media.url, rendition)}
            alt=""
            onLoad={(event) => onFit(facebookFillsTallFrame(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight))}
            onError={() => {
              setImageFailed(true);
              onFit(false);
            }}
          />
        )}
        {isVideo && !failed && !video.playing && <PlayGlyph />}
      </button>
      {isVideo && !failed && video.playing && <SoundToggle muted={video.muted} onToggle={video.toggleMute} />}
    </>
  );
}

function FacebookReel({ card, onOpenMedia, onToggleCaption }: FacebookPostPreviewProps) {
  const label = useTileLabel(card);
  const [fits, setFits] = useState(false);
  const media = card.media[0];
  const overlay = (
    <>
      <Badge variant="waiting-on-a-reply" size="tiny" label={card.labels.badge} className={styles.mediaPill} />
      <div className={styles.reelOverlay}>
        <div className={styles.onMediaName}>
          <PageAvatar card={card} />
          <span className={styles.reelName} dir="auto" data-testid="facebook-page-name">
            {facebookPageName(card)}
          </span>
        </div>
        {card.caption && (
          <FacebookCaption
            caption={card.caption}
            seeMore={card.labels.seeMore}
            seeLess={card.labels.seeLess}
            className={styles.reelCaption}
            limit={FACEBOOK_REEL_CAPTION_LIMIT}
            hashtags={false}
            scrollable
            onToggle={onToggleCaption}
          />
        )}
      </div>
    </>
  );
  return (
    <div className={styles.reel} data-testid="facebook-reel">
      <TallFrame fits={!!media && fits}>
        {media && (
          <TallMedia
            key={media.url}
            media={media}
            label={label(media, 0)}
            rendition={FACEBOOK_RENDITIONS.reel}
            onOpen={() => onOpenMedia(media, 0)}
            onFit={setFits}
          />
        )}
        {overlay}
      </TallFrame>
    </div>
  );
}

function FacebookStory({ card, onOpenMedia }: FacebookPostPreviewProps) {
  const { t } = useTranslation();
  const items = card.media;
  const label = useTileLabel(card, items.length);
  const [index, setIndex] = useState(0);
  const [fits, setFits] = useState(false);
  const current = items[Math.min(index, Math.max(items.length - 1, 0))];
  const overlay = (
    <div className={styles.storyTop}>
      <div className={styles.bars} data-testid="facebook-story-bars">
        {items.map((item, at) => (
          <span key={`${item.url}-${at}`} className={at <= index ? `${styles.bar} ${styles.barSeen}` : styles.bar} data-seen={at <= index} />
        ))}
      </div>
      <div className={styles.storyHead}>
        <div className={styles.onMediaName}>
          <PageAvatar card={card} />
          <span className={styles.storyName} dir="auto" data-testid="facebook-page-name">
            {facebookPageName(card)}
          </span>
        </div>
        <Badge variant="waiting-on-a-reply" size="tiny" label={card.labels.badge} className={styles.pill} />
      </div>
    </div>
  );
  return (
    <div className={styles.story} data-testid="facebook-story">
      <TallFrame fits={!!current && fits}>
        {current && (
          <TallMedia
            key={`${index}-${current.url}`}
            media={current}
            label={label(current, index)}
            rendition={FACEBOOK_RENDITIONS.story}
            onOpen={() => onOpenMedia(current, index)}
            onFit={setFits}
          />
        )}
        {overlay}
        {items.length > 1 && index > 0 && (
          <button
            type="button"
            className={`${styles.step} ${styles.stepBack}`}
            aria-label={t('calendar.toolbar.previous')}
            onClick={() => setIndex(index - 1)}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
        )}
        {items.length > 1 && index < items.length - 1 && (
          <button
            type="button"
            className={`${styles.step} ${styles.stepNext}`}
            aria-label={t('calendar.toolbar.next')}
            onClick={() => setIndex(index + 1)}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        )}
      </TallFrame>
    </div>
  );
}
